import {Component, forwardRef, Input, OnChanges} from '@angular/core';
import {FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {CustomValidators} from '../validator/customValidators';
import {PrivatePerson} from '../model/private-person.model';
import {CustomerService} from '../../core/services/customer.service';
import {SnackBarService} from '../../core/services/snack-bar.service';
import {DictionaryPosition} from '../model/dictionary.model';
import {AbstractControlComponent} from '../abstract-control-value-accessor/abstract-control.component';
import {IEmail} from '../model/interface/email.model.interface';
import {IPhone} from '../model/interface/phone.model.interface';
import {AddCustomerAddressComponent} from '../add-customer-address/add-customer-address.component';
import {AddCustomerEmailComponent} from '../add-customer-email/add-customer-email.component';
import {AddCustomerPhoneComponent} from '../add-customer-phone/add-customer-phone.component';
import {DialogOpener} from '../utils/dialog-opener';
import {IEditPersonalInfo} from '../model/interface/edit-personal-info.model.interface';
import {IAddress} from '../model/interface/address.model.interface';

@Component({
  selector: 'app-private-person-info',
  templateUrl: './private-person-info.component.html',
  styleUrls: ['./private-person-info.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrivatePersonInfoComponent),
      multi: true,
    },
  ],
})
export class PrivatePersonInfoComponent extends AbstractControlComponent implements OnChanges {
  @Input() standalone: boolean;
  @Input() privatePerson: PrivatePerson;
  @Input() requiredContact = false;
  personalInfo: FormGroup;

  constructor(private customerService: CustomerService,
              private formBuilder: FormBuilder,
              private snackBarService: SnackBarService,
              private dialogOpener: DialogOpener) {
    super();
    this.createForm();
  }

  ngOnChanges() {
    this.updateForm();
  }

  ngOnInit() {
    this.subscribeForm();
  }

  private createForm() {
    this.personalInfo = this.formBuilder.group({
      id: [],
      name: [null, [Validators.required, CustomValidators.nameValidator]],
      secondName: [null, CustomValidators.onlyLettersValidator],
      surname: [null, [Validators.required, CustomValidators.surnameValidator]],
      pesel: [null, [CustomValidators.peselValidator]],
      individualNumber: [null],
    });
  }

  private updateForm() {
    if (!this.privatePerson) {
      return;
    }

    this.personalInfo.patchValue(
      {
        id: this.privatePerson.id,
        name: this.privatePerson.name,
        secondName: this.privatePerson.secondName,
        surname: this.privatePerson.surname,
        pesel: this.privatePerson.pesel,
        individualNumber: this.privatePerson.individualNumber
      }
    );
  }

  writeValue(privatePerson: PrivatePerson) {
    this.privatePerson = privatePerson;
    this.updateForm();
  }

  registerOnChange(fn: any): void {
    this.personalInfo.valueChanges.subscribe(fn);
  }


  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.personalInfo.disable() : this.personalInfo.enable();
  }

  updatePrivatePerson() {
    const value = this.getUpdatePrivatePersonPayload();
    this.customerService.updatePrivatePerson(value)
      .subscribe(
        (privatePerson: PrivatePerson) => {
          this.snackBarService.display(DictionaryPosition.PRIVATE_PERSON_INFO_CHANGED);
          this.privatePerson = privatePerson;
        }
      );
  }

  private getUpdatePrivatePersonPayload(): IEditPersonalInfo {
    const value = this.personalInfo.value;
    return {
      id: this.privatePerson.id,
      name: value.name,
      secondName: value.secondName,
      surname: value.surname,
      pesel: value.pesel,
      individualNumber: value.individualNumber
    };
  }

  private subscribeForm() {
    this.personalInfo.valueChanges.subscribe(person => {
      if (this.personalInfo.valid) {
        this.privatePerson = new PrivatePerson(person);
      }
    });
  }

  deleteEmail(emailToDelete: IEmail) {
    const index = this.privatePerson.emails
      .data
      .findIndex(el => el.id === emailToDelete.id);
    this.privatePerson.emails.data.splice(index, 1);
  }

  deletePhone(phoneToDelete: IPhone) {
    const index = this.privatePerson.phones
      .data
      .findIndex(el => el.id === phoneToDelete.id);
    this.privatePerson.phones.data.splice(index, 1);
  }

  deleteAddress(addressToDelete: IAddress) {
    const index = this.privatePerson.addresses
      .data
      .findIndex(ad => ad.id === addressToDelete.id);
    this.privatePerson.addresses.data.splice(index,1)
  }

  addAddress() {
    return this.dialogOpener.open(AddCustomerAddressComponent, {
      data: this.privatePerson,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }

  addEmail() {
    return this.dialogOpener.open(AddCustomerEmailComponent, {
      data: this.privatePerson,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }

  addPhone() {
    return this.dialogOpener.open(AddCustomerPhoneComponent, {
      data: this.privatePerson,
      width: '80vh'
    }).afterClosed()
      .subscribe();
  }
}
